import React, {Suspense} from 'react';

import {type SelectOption} from '@famly/mf_input_select';
import * as GlobalEvent from 'web-app/services/global-event';
import {PortalModal} from 'web-app/react/components/modals/modals';
import Spinner from 'web-app/react/components/loading/spinner/spinner';
import type {useAutomaticInvoicingData} from 'web-app/react/routes/account/institution/automatic-invoicing-config/hooks/use-automatic-invoicing-data';

import {AddNewTemplateModal} from '../templates/add-new-template-modal';
import {transformPricingGroupOptionsToPricingGroupFormItems} from '../add-new-item/transformers';
import type {Handlers} from '../hooks/use-handlers';
import type {State} from '../types';
import {type useSubmitNewItem} from '../hooks/use-submit-new-item';

const AddNewItemModal = React.lazy(() =>
    import('../add-new-item/add-new-item-modal').then(mod => ({default: mod.AddNewItemModal})),
);
const AddNewDiscountModal = React.lazy(() =>
    import('../add-new-discount/add-new-discount-modal').then(mod => ({default: mod.AddNewDiscountModal})),
);
const MonthlyBillingFrequencySetupModal = React.lazy(() =>
    import('../auto-billing-setup/monthly-billing-frequency-setup-modal').then(mod => ({
        default: mod.MonthlyBillingFrequencySetupModal,
    })),
);
const WeeklyBillingFrequencySetupModal = React.lazy(() =>
    import('../auto-billing-setup/weekly-billing-frequency-setup-modal').then(mod => ({
        default: mod.WeeklyBillingFrequencySetupModal,
    })),
);
const OverrideTuitionPriceModal = React.lazy(() =>
    import('../override-tuition-price/override-tuition-price-modal').then(mod => ({
        default: mod.OverrideTuitionPriceModal,
    })),
);
const BiweeklyBillingFrequencySetupModal = React.lazy(() =>
    import('../auto-billing-setup/biweekly-billing-frequency-setup-modal').then(mod => ({
        default: mod.BiweeklyBillingFrequencySetupModal,
    })),
);
const EmptyPlanModal = React.lazy(() =>
    import('./empty-plan-modal').then(mod => ({
        default: mod.EmptyPlanModal,
    })),
);

type Props = {
    siteId: string;
    state: State;
    handlers: Handlers;
    pricingGroupOptions: Array<SelectOption>;
    automaticInvoicing: ReturnType<typeof useAutomaticInvoicingData>;
    submitNewItem: ReturnType<typeof useSubmitNewItem>;
    submission: {
        isSavePlanDisabled: boolean;
        isSubmissionLoading: boolean;
        handleSubmit: VoidFunction;
        handleSavePlan: VoidFunction;
    };
};

export const Modal = ({
    siteId,
    state,
    handlers,
    automaticInvoicing,
    pricingGroupOptions,
    submission: {isSavePlanDisabled, isSubmissionLoading, handleSavePlan, handleSubmit},
    submitNewItem: {handleNewItemSubmit, isSessionPackageCreationLoading, isProductCreationLoading},
}: Props) => {
    const {
        displayedModal,
        billingPeriodState: {selectedBillingPeriod},
        isAutomaticDecouplingEnabled,
        template: {newTemplateName},
        discountState: {allDiscounts, selectedDiscounts},
        selectedBillingType,
    } = state;

    const {
        handleModalClose,
        handleNewTemplateNameChange,
        handleDiscountChange,
        handleEmptyPlanSetRange,
        handleCancelEmptyPlanModal,
        handleSaveTuitionPriceOverride,
        handleAllDiscountsUpdate,
    } = handlers;

    const renderModal = () => {
        switch (displayedModal.type) {
            case 'monthly': {
                return (
                    <PortalModal id="monthlyBillingFrequencySetupModal">
                        <MonthlyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            selectedBillingPeriod={selectedBillingPeriod}
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'weekly': {
                return (
                    <PortalModal id="weeklyBillingFrequencySetupModal">
                        <WeeklyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            selectedBillingPeriod={selectedBillingPeriod}
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'monthly-partial': {
                return (
                    <PortalModal id="monthlyBillingFrequencySetupModal">
                        <MonthlyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            isPartial
                            selectedBillingPeriod={selectedBillingPeriod}
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'weekly-partial': {
                return (
                    <PortalModal id="monthlyBillingFrequencySetupModal">
                        <WeeklyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            isPartial
                            selectedBillingPeriod={selectedBillingPeriod}
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'biweekly': {
                return (
                    <PortalModal id="biweeklyBillingFrequencySetupModal">
                        <BiweeklyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'biweekly-partial': {
                return (
                    <PortalModal id="biweeklyBillingFrequencySetupModal">
                        <BiweeklyBillingFrequencySetupModal
                            onClose={handleModalClose}
                            onSubmit={handleSubmit}
                            automaticInvoicing={automaticInvoicing}
                            isPartial
                            isAutomaticDecouplingEnabled={isAutomaticDecouplingEnabled}
                        />
                    </PortalModal>
                );
            }
            case 'empty-plan': {
                return (
                    <EmptyPlanModal
                        state={state}
                        handleSetRange={handleEmptyPlanSetRange}
                        handleCancel={handleCancelEmptyPlanModal}
                        saveButton={{
                            handleSave: handleSubmit,
                            isDisabled: isSavePlanDisabled,
                            isLoading: isSubmissionLoading,
                        }}
                    />
                );
            }
            case 'add-template': {
                return (
                    <AddNewTemplateModal
                        onClose={handleModalClose}
                        onSave={handleSavePlan}
                        handleNewTemplateNameChange={handleNewTemplateNameChange}
                        templateName={newTemplateName ?? ''}
                    />
                );
            }
            case 'addTuition': {
                return (
                    <AddNewItemModal
                        onClose={handleModalClose}
                        pricingGroups={transformPricingGroupOptionsToPricingGroupFormItems(pricingGroupOptions)}
                        siteId={siteId}
                        submitButton={{
                            onClick: handleNewItemSubmit,
                            loading: isSessionPackageCreationLoading || isProductCreationLoading,
                        }}
                        type="tuition"
                        billingType={selectedBillingType}
                    />
                );
            }
            case 'addFee': {
                return (
                    <AddNewItemModal
                        onClose={handleModalClose}
                        pricingGroups={transformPricingGroupOptionsToPricingGroupFormItems(pricingGroupOptions)}
                        siteId={siteId}
                        submitButton={{
                            onClick: handleNewItemSubmit,
                            loading: isSessionPackageCreationLoading || isProductCreationLoading,
                        }}
                        type="fee"
                        billingType={displayedModal.payload.billingType}
                    />
                );
            }
            case 'addDiscount': {
                return (
                    <AddNewDiscountModal
                        onClose={handleModalClose}
                        billingType={displayedModal.billingType}
                        onDiscountChange={handleDiscountChange}
                        onAllDiscountsUpdate={handleAllDiscountsUpdate}
                        siteId={siteId}
                        allDiscounts={allDiscounts}
                        selectedDiscounts={selectedDiscounts}
                    />
                );
            }
            case 'overrideTuitionPrice': {
                return (
                    <OverrideTuitionPriceModal
                        onClose={handleModalClose}
                        tuition={displayedModal.payload.tuition}
                        onSave={handleSaveTuitionPriceOverride}
                        pricingGroupId={displayedModal.payload.selectedPricingGroup}
                    />
                );
            }
            case 'error':
                GlobalEvent.updateError(displayedModal.errorMessage);
                return null;
            default:
                return null;
        }
    };

    return <Suspense fallback={<Spinner centered margined />}>{renderModal()}</Suspense>;
};
